<script setup lang="ts">
import { nextTick, computed, ref } from 'vue';
import '@patsnap-ui/icon/assets/solid/warning.svg';
import SvgIcon from '@/presentation/components/common/svg-icon/SvgIcon.vue';
import DisclosureSideLoad from './DisclosureSideLoad.vue';
import { useI18n } from 'vue-i18n';
import { useAiDisclosure } from '@/composables/use-ai-disclosure';
import IconDisclosure from '@/presentation/components/icons/IconDisclosure.vue';
import UserInput from '@/presentation/components/user-input/UserInput.vue';
import UserInputExample from '@/presentation/components/user-input-example/UserInputExample.vue';
import ConfirmDialog from '@/presentation/components/common/confirm-dialog/ConfirmDialog.vue';
import { OPEN_DELAY } from '@/constants';
import { ElTooltip } from 'element-plus';
import { CheckSensitiveWordService } from '@/services/common/check-sensitive-word';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import AiDisclosureInstructionCn from '@/presentation/assets/images/ai-disclosure-instruction-cn.png';
import AiDisclosureInstructionEn from '@/presentation/assets/images/ai-disclosure-instruction-en.png';
import AiDisclosureInstructionJp from '@/presentation/assets/images/ai-disclosure-instruction-jp.png';
import DisclosureAmount from './DisclosureAmount.vue';
import { useUsageAmount } from '@eureka/edition-upgrade/src/composables/use-usage-amount';

const { usageAmount, isUsageAmountReady } = useUsageAmount();

const checkSensitiveWordService = injectService<CheckSensitiveWordService>(
    CheckSensitiveWordService
);

const { t, locale } = useI18n();
const { aiDisclosureService, aiDisclosureData } = useAiDisclosure();

const showTechMeansDialog = ref<boolean>(false);
const loadingSide = ref<boolean>(false);
const loadingGenerate = ref<boolean>(false);
const isCheckingSensitiveWord = ref<boolean>(false);
const userInputTechMeans = ref<string>('');

const instructionImage = computed(() => {
    switch (locale.value) {
    case 'cn':
        return AiDisclosureInstructionCn;
    case 'jp':
        return AiDisclosureInstructionJp;
    default:
        return AiDisclosureInstructionEn;
    }
});

async function handleStartGenerate() {
    loadingGenerate.value = true;
    loadingSide.value = true;
    try {
        const hasTechMeans = await aiDisclosureService.startGenerate();

        if (hasTechMeans) {
            loadingGenerate.value = false;
        } else {
            showTechMeansDialog.value = true;
        }
    } catch (e) {
        loadingGenerate.value = false;
        loadingSide.value = false;
        throw e;
    }
}

function goResult() {
    aiDisclosureService.goResult();
}

function handelLoaded() {
    loadingSide.value = false;
    goResult();
}

async function techMeansDialogConfirm() {
    isCheckingSensitiveWord.value = true;
    const res = await checkSensitiveWordService.checkSensitiveWord(userInputTechMeans.value);
    if (res) {
        isCheckingSensitiveWord.value = false;
    } else {
        await aiDisclosureService.putTechMeans(userInputTechMeans.value);
        isCheckingSensitiveWord.value = false;
        showTechMeansDialog.value = false;
        nextTick(() => {
            loadingSide.value = true;
        });
    }
}

async function techMeansDialogCancel() {
    showTechMeansDialog.value = false;
    loadingGenerate.value = false;
    loadingSide.value = false;
}

function handleInputChange(input: string) {
    aiDisclosureService.updateInput(input);
}

function handleUseExample(input: string) {
    if (loadingSide.value || loadingGenerate.value) return;
    handleInputChange(input);
}
</script>

<template>
    <div class="disclosure-input m-auto flex w-[640px]">
        <div>
            <div
                class="disclosure-input-header flex items-center justify-between pb-6 pt-12 "
            >
                <div class="flex items-center">
                    <IconDisclosure class="th-btn-icon mr-2 h-10 w-10" />
                    <span class="text-[18px] font-semibold text-black">
                        {{ t('aiDisclosure.disclosureTitle') }}
                    </span>
                </div>

                <ElTooltip
                    effect="light"
                    :visibleArrow="false"
                    placement="bottom-end"
                    popperClass="disclosure-tooltip-tip"
                    :openDelay="OPEN_DELAY"
                >
                    <div
                        class="disclosure-tip group flex cursor-pointer items-center"
                    >
                        <SvgIcon
                            class="mr-1 h-6 w-6 fill-grey-55 group-hover:fill-grey-450"
                            icon="SolidInfo"
                        />

                        <span class="text-xs text-grey-450 group-hover:text-black">
                            {{ t('aiDisclosure.tip') }}
                        </span>
                    </div>

                    <template #content>
                        <img
                            :width="500"
                            :src="instructionImage"
                            alt="ai-disclosure-tip"
                        >
                    </template>
                </ElTooltip>
            </div>

            <div class="mb-2 text-xs text-grey-90">
                {{ t('aiDisclosure.disclaimer') }}
            </div>

            <UserInput
                :originalInput="aiDisclosureData.input"
                :placeholder="t('aiDisclosure.originInputPlaceholder')"
                :noSupportLangTip="t('aiDisclosure.noSupportLanguage')"
                :submitBtnText="t('aiDisclosure.startGenerate')"
                :loadingSubmitBtn="loadingSide || loadingGenerate || !isUsageAmountReady"
                @submit="handleStartGenerate"
                @handleInput="handleInputChange"
            />

            <DisclosureAmount />

            <UserInputExample
                class="mb-4"
                :example="t('aiDisclosure.exampleInput')"
                @handleExampleChange="handleUseExample"
            />
        </div>

        <DisclosureSideLoad v-if="loadingSide" @loaded="handelLoaded" />

        <ConfirmDialog
            v-if="showTechMeansDialog"
            class="input-tech-means-dialog"
            :title="t('aiDisclosure.techMeansDialogTitle')"
            :confirmText="t('aiDisclosure.techMeansDialogConfirm')"
            :disable="!userInputTechMeans.length"
            :isLoadingSubmit="isCheckingSensitiveWord"
            data-theme="patsnapRD"
            @cancel="techMeansDialogCancel"
            @confirm="techMeansDialogConfirm"
        >
            <div class="pb-2 text-sm text-black">
                {{
                    t('aiDisclosure.addTechMeansDesc')
                }}
            </div>
            <textarea
                v-model="userInputTechMeans"
                class="h-[264px] w-full resize-none rounded border border-grey-55 px-3 py-1.5"
                :placeholder="t('aiDisclosure.addTechMeansPlaceholder')"
            />
        </ConfirmDialog>
    </div>
</template>

<style lang="scss">
.disclosure-tooltip-tip {
    &.el-tooltip__popper {
        border: 0 !important;
        padding: 0 !important;
        margin: 4px 0 !important;
        box-shadow: 0 0 4px 0 #00000014, 0 2px 6px 0 #00000014;

    }
}
</style>
